import { AppState } from '@redux/store'
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { Form } from '@typesApp/DynamicComponent'
import { Insurer, InsurerErrorType } from '@typesApp/Insurer'
import { FormChangeState } from '@typesApp/InsuranceForm'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { connectorSlice } from './connectorSlice'

type DynamicFormsState = {
  forms: Form[]
  index: number
}

type FormState = {
  insurer?: Insurer
  dynamicForms: DynamicFormsState
  changeState?: FormChangeState
  formsReady?: boolean
}

const initialState: FormState = {
  dynamicForms: {
    forms: [],
    index: -1,
  },
}

const REDUCER_PATH = 'forms'

export const formsSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setForms(state, action: PayloadAction<Form[]>) {
      state.dynamicForms.forms = action.payload
      if (state.dynamicForms.index === -1 && action.payload.length) {
        state.dynamicForms.index = 0
      }
    },
    initLastForm(state) {
      state.dynamicForms.index = (state.dynamicForms.forms?.length ?? 0) - 1
    },
    resetLastForm(state, action: PayloadAction<number | undefined>) {
      const index = action.payload ?? 0
      if (index !== -1 && index < state.dynamicForms.forms?.length) {
        state.dynamicForms.index = index
      }
      state.formsReady = true
    },
    selectInsurer(state, action: PayloadAction<{ insurer: Insurer; forms: Form[] }>) {
      state.insurer = action.payload.insurer
      state.dynamicForms.forms = action.payload.forms
    },
    nextForm(state) {
      const index = state.dynamicForms.index
      const numForms = state.dynamicForms.forms?.length ?? 0
      state.dynamicForms.index = index !== -1 && index < numForms - 1 ? index + 1 : index
    },
    previousForm(state) {
      const index = state.dynamicForms.index
      state.dynamicForms.index = index !== -1 && index > 0 ? index - 1 : index
    },
    formAtIndex(state, action: PayloadAction<number | undefined>) {
      const index = action.payload ?? -1
      state.dynamicForms.index = index
    },
    setFormChangeState(state, action: PayloadAction<FormChangeState>) {
      return {
        ...state,
        changeState: action.payload,
      }
    },
    resetFormChangeState(state) {
      return {
        ...state,
        changeState: undefined,
      }
    },
    resetForms(state) {
      state.changeState = undefined
      state.insurer = undefined
      state.formsReady = undefined
      if (state.dynamicForms?.forms) {
        state.dynamicForms.index = state.dynamicForms.index !== -1 ? 0 : -1
      }
    },
    resetFormsInitialState() {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * Reset selected insurer
     */
    builder.addMatcher(
      isAnyOf(
        connectorSlice.actions.connectorOrderComplete,
        claimRecordsApi.endpoints.createClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.nullifyClaim.matchFulfilled,
        claimRecordsApi.endpoints.submitClaim.matchFulfilled
      ),
      state => {
        state.insurer = undefined
      }
    )

    /**
     * Reset to initial state on session expiry or logout
     */
    builder.addMatcher(
      isAnyOf(connectorSlice.actions.sessionExpired, connectorSlice.actions.connectorLogoutInProgress),
      () => {
        return initialState
      }
    )
  },
})

export const {
  setForms,
  selectInsurer,
  nextForm,
  previousForm,
  formAtIndex,
  resetForms,
  initLastForm,
  resetLastForm,
  setFormChangeState,
  resetFormChangeState,
  resetFormsInitialState,
} = formsSlice.actions

export const dynamicFormsSelector = (state: AppState): DynamicFormsState => state.forms.dynamicForms
export const selectedInsurerSelector = (state: AppState): Insurer | undefined => state.forms.insurer
export const formChangeStateSelector = (state: AppState): FormChangeState | undefined => state.forms.changeState
export const formsReadySelector = (state: AppState): boolean => state.forms.formsReady === true
